import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import labService from './labService.js';
import authService from '../auth/authService';
import './css/Lab.css';
import './css/ChooseSubstance.css';
import Visualization from './Visualization.js';
import { getCoreColor } from './helpers/colorHelper';
import LoadingScreen from './LoadingScreen';


const ChooseSubstance = ({ isCollapsed, toggleSidebar }) => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Zamiast nazwy 'SiO2', przechowujemy ID wybranego endpointa
  const [selectedCoreId, setSelectedCoreId] = useState(null);
  const [analysisName, setAnalysisName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEndpoints = async () => {
      const user = authService.getCurrentUser();
      if (!user || !user.access) {
        setError("You must be logged in to view substances.");
        setLoading(false);
        return;
      }

      try {
        const response = await labService.getEndpoints(user.access);
        const data = response.data;
        setEndpoints(data);

        // Automatycznie zaznacz pierwszą dostępną substancję (która ma modele)
        const firstAvailable = data.find(ep => ep.ml_models && ep.ml_models.length > 0);
        if (firstAvailable) {
          setSelectedCoreId(firstAvailable.id);
        }
      } catch (err) {
        console.error("Error fetching endpoints:", err);
        setError("Failed to load substances.");
      } finally {
        setLoading(false);
      }
    };

    fetchEndpoints();
  }, []);

  const closeSelectModels = () => {
    navigate('/laboratory');
  };

  const handleNext = () => {
    if (!selectedCoreId) return;
    if (analysisName.trim() === '') return;

    // Zapisujemy wybrane ID oraz nazwę do localStorage (zgodnie z logiką ze starej apki)
    localStorage.setItem('selectedEndpoint', JSON.stringify(selectedCoreId));
    localStorage.setItem('analysisName', JSON.stringify(analysisName));

    // Przechodzimy do następnego kroku
    navigate('/define-endpoints');
  };

  const formatText = (input) => {
    if (typeof input !== 'string') return input;
    return input.split(/(\d+)/).map((part, i) =>
      /\d+/.test(part) ? <sub key={i} style={{ fontSize: '0.6em', verticalAlign: 'sub' }}>{part}</sub> : part
    );
  };


  // Obiekt aktualnie wybranego endpointa (do przekazania nazwy do wizualizacji)
  const selectedEndpointObj = endpoints.find(ep => ep.id === selectedCoreId);
  const coreName = selectedEndpointObj ? selectedEndpointObj.endpoint_name : 'Nanoform';
  const coreColor = getCoreColor(coreName);

  if (loading) {
    return <LoadingScreen />;
  }
  if (error) return <div style={{ marginTop: '100px', textAlign: 'center', color: 'red' }}>{error}</div>;

  return (
    <div className='lab-container'>
      <div className='new-pred-container'>
        <div className='np-tabs'>
          <button onClick={closeSelectModels} className='x-but'>
            <svg viewBox="0 0 24 24" focusable="false" className='icon-x' aria-hidden="true"><path fill="currentColor" d="M.439,21.44a1.5,1.5,0,0,0,2.122,2.121L11.823,14.3a.25.25,0,0,1,.354,0l9.262,9.263a1.5,1.5,0,1,0,2.122-2.121L14.3,12.177a.25.25,0,0,1,0-.354l9.263-9.262A1.5,1.5,0,0,0,21.439.44L12.177,9.7a.25.25,0,0,1-.354,0L2.561.44A1.5,1.5,0,0,0,.439,2.561L9.7,11.823a.25.25,0,0,1,0,.354Z"></path></svg>
          </button>
          <div className='np-steps active-tab-cm'>
            <p className='p-step'>STEP 1</p>
            <p className='mar-btm-few'>Select substance</p>
            <div className="step-progress"></div>
          </div>
          <div className='np-steps'>
            <p className='p-step'>STEP 2</p>
            <p className='mar-btm-few'>Select endpoints</p>
          </div>
          <div className='np-steps'>
            <p className='p-step'>STEP 3</p>
            <p className='mar-btm-few'>Define nanoform</p>
          </div>
        </div>

        <div className='new-study-wizard-body'>
          <div className='new-study-wizard-configurator'>
            <div className="select-substance">
              <div className="core-container">
                <h2 className="core-header">Core</h2>
              </div>
              <div className="divider"></div>

              <div role="radiogroup" className="radio-choser-group">
                {endpoints.map((ep) => {
                  const hasModels = ep.ml_models && ep.ml_models.length > 0;
                  const isActive = selectedCoreId === ep.id;
                  const epColor = getCoreColor(ep.endpoint_name);

                  return (
                    <div key={ep.id} className="radio-choser-wrapper" style={{ opacity: hasModels ? 1 : 0.5 }}>
                      <label className="radio-choser-wrapper-label" style={{ cursor: hasModels ? 'pointer' : 'not-allowed' }}>
                        <input
                          type="radio"
                          className="radio-choser-input"
                          value={ep.id}
                          checked={isActive}
                          onChange={() => hasModels && setSelectedCoreId(ep.id)}
                          disabled={!hasModels}
                          style={{ display: 'none' }}
                        />
                        <div className={`radio-choser-checker-wrapper ${isActive ? 'active-core' : ''}`}>
                          <div className="radio-choser-checker-wrapper-inside" style={{ display: 'flex', alignItems: 'center' }}>
                            <svg
                              viewBox="0 0 512 512"
                              height="2rem"
                              width="2rem"
                              style={{ overflow: 'visible' }}
                            >
                              <circle
                                className="core-svg-circle"
                                cx="256"
                                cy="256"
                                r="210"
                                fill={epColor}
                              />
                            </svg>
                            <span className="core-label">
                              {formatText(ep.endpoint_name)}
                              {!hasModels && <span style={{ fontSize: '10px', color: '#ff4d4f', marginLeft: '8px', fontWeight: 'bold' }}>COMING SOON</span>}
                            </span>
                          </div>
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>

              <div role="group" className="prediction-form">
                <label className="prediction-form-label" id="research-name-label" htmlFor="research-name">Prediction name</label>
                <input
                  className='prediction-name-input'
                  type="text"
                  placeholder='Name'
                  value={analysisName}
                  onChange={(e) => setAnalysisName(e.target.value)}
                  maxLength={65}
                />
              </div>

              <div className="next-btn-wrapper">
                <div className="next-btn-wrapper-one-button">
                  <button
                    onClick={handleNext}
                    className={analysisName.trim().length > 0 && selectedCoreId ? 'next-button' : 'disabled-button'}
                    disabled={analysisName.trim().length === 0 || !selectedCoreId}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className='new-study-wizard-visualization'>
            <Visualization
              core={{ name: coreName }}
              coreModification={null}
              coreColor={coreColor}
              surfaceFunctionalization={false}
              surfaceFunctionalizationColor="#9ed2ff"
              lineColor="#7fb13a"
              isNanoformImageGenerated={false}
              visualizationType={'inconclusive'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChooseSubstance;
